import React, { useEffect, useState } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { Button } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ListTable from '../../components/ListTable';
import EditAreaModal from '../../components/EditAreaModal';
import Container from '../../layouts/Container';
import { getAreas, createArea, deleteArea } from '../../services/project/areaService';
import { getRegionsWithProvinces } from '../../services/project/regionService';
import InputWithButton from '../../components/InputWithButton';
import { Pencil, XOctagon  } from 'react-bootstrap-icons';
import AnchorTag from '../../components/AnchorTag';
import { useProjects } from '../../context/ProjectContext';

function ActivateUser() {
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedArea, setSelectedArea] = useState(null);
  const [provinces, setProvinces] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const { projectId, regionId } = useParams();
  const { isProjectReadOnly } = useProjects();

  useEffect(() => {
    const fetchRegions = async () => {
      try {
        const response = await getRegionsWithProvinces(projectId);
        const region = response.regions.find(r => r.id == regionId);
        if (region?.provinces) {
          setProvinces(region.provinces);
        }
      } catch(error) {
        console.log(error);
      }
    }
    if (!provinces) {
      fetchRegions();
    }
    fetchAreas();
    setLoading(false);
  }, []);


  const handleEditAreas = (area) => {
    setSelectedArea(area);
    setShowModal(true);
  };

  
  const fetchAreas = async () => {
    try {
      const response = await getAreas(projectId, regionId);
      if (!response.success) {
        toast.error(response.data?.errorMessage || 'Error fetching areas.');
        return;
      }
      setAreas(response.areas);
      if (showModal) setSelectedArea(response.areas.find(r => r.id === selectedArea.id))
    } catch (error) {
      console.error('Error fetching areas:', error);
      toast.error(error.response?.data?.errorMessage || 'Something went wrong');
    }
  };
  
  const handleCreateArea = async (projectId, regionId, areaName) => {
    try {
      const response = await createArea(projectId, regionId, areaName);
      if (!response.success) {
        toast.error(response.data.error);
        return;
      }
      fetchAreas();
      toast.success(response.message);
    } catch (error) {
      console.error('Error creating region:', error);
      toast.error(error.response?.data?.errorMessage || 'Something went wrong');
    }
  }

  const handleDeleteArea = async (projectId, regionId, areaId) => {
    const confirmed = window.confirm('Are you sure?');
    if (!confirmed) return;
    try {
      const response = await deleteArea(projectId, regionId, areaId);
    
      if (!response.success) {
        toast.error(response.data.error);
        return;
      }
      fetchAreas();
      toast.success(response.message);
    } catch (error) {
      console.error('Error deleting area:', error);
      toast.error(error.response?.data?.errorMessage || 'Something went wrong');
    }
  };

  const columnHelper = createColumnHelper();

  const columns = [
    columnHelper.accessor(
      row => row.id,
      {
        id: 'id',
        header: 'ID',
        cell: info => info.getValue(),
      }
    ),
    columnHelper.accessor('name', {
      header: 'Area Name',
      cell: info => info.getValue(),
    }),

    columnHelper.accessor('headcount', {
      header: 'Headcount',
      cell: info => info.getValue() || 0,
    }),

    columnHelper.accessor('dailyQuota', {
      header: 'Daily Quota',
      cell: info => info.getValue() || 0,
    }),

    columnHelper.accessor(
      row => row.provinces?.map(p => p.name).join(', ') || '-',
      {
        id: 'provinces',
        header: 'Provinces',
        cell: info => info.getValue(),
      }
    ),

    (isProjectReadOnly ? false :columnHelper.display({
      id: 'actions',
      header: 'Action',
      cell: info => {
        const area = info.row.original;
        return (
          <div className="d-flex gap-2">
            <AnchorTag onClick={() => handleEditAreas(area)} hoverText='Edit'>
              <Pencil />
            </AnchorTag>
            <AnchorTag onClick={() => handleDeleteArea(projectId, regionId, area.id)} hoverText='Delete'>
              <XOctagon />
            </AnchorTag>
          </div>
        );
      },
    })),
  ].filter(Boolean);

  return (
    <Container>
      <h2 style={{ marginBottom: '1rem' }}>Area List</h2>
      { !isProjectReadOnly && <InputWithButton placeholder="Enter area name" title="Add New" onSubmit={(name) =>handleCreateArea(projectId, regionId, name)}/> }
      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          <ListTable tableId='area-list' data={areas} columns={columns} />
          { (showModal && selectedArea) && (
            <EditAreaModal
              projectId={projectId}
              regionId={regionId}
              area={selectedArea}
              show={showModal}
              onAreaMutation={fetchAreas}
              provinces={provinces}
              onClose={() => setShowModal(false)}
            />
          )}

        </>

      )}
    </Container>
  );
}

export default ActivateUser;
